const DIR_KEY_MAP = {
    up: "ArrowUp",
    down: "ArrowDown",
    left: "ArrowLeft",
    right: "ArrowRight",
};

export default class Input {
    constructor({ onDig, onReset }) {
        this.onDig = onDig;
        this.onReset = onReset;

        this.keys = Object.create(null);
        this.virtualKeyOwners = Object.create(null);

        this.onKeyDown = (event) => {
            if (
                ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Space"].includes(
                    event.code,
                )
            ) {
                event.preventDefault();
            }

            this.keys[event.code] = true;
            if (event.code === "Space" && !event.repeat) this.onDig();
            if (event.code === "KeyR" && !event.repeat) this.onReset();
        };

        this.onKeyUp = (event) => {
            this.keys[event.code] = false;
        };

        this.onBlur = () => {
            this.clear();
        };

        this.onVisibility = () => {
            if (document.hidden) this.clear();
        };

        window.addEventListener("keydown", this.onKeyDown, { passive: false });
        window.addEventListener("keyup", this.onKeyUp);
        window.addEventListener("blur", this.onBlur);
        document.addEventListener("visibilitychange", this.onVisibility);

        this.bindTouch();
        this.bindGestures();
    }

    setVirtualKey(keyCode, pointerId, pressed) {
        if (!this.virtualKeyOwners[keyCode]) {
            this.virtualKeyOwners[keyCode] = new Set();
        }
        const owners = this.virtualKeyOwners[keyCode];

        if (pressed) {
            owners.add(pointerId);
            this.keys[keyCode] = true;
        } else {
            owners.delete(pointerId);
            if (owners.size === 0) this.keys[keyCode] = false;
        }
    }

    bindHoldButton(element, onDown, onUp) {
        element.addEventListener(
            "pointerdown",
            (event) => {
                event.preventDefault();
                event.stopPropagation();
                try {
                    element.setPointerCapture(event.pointerId);
                } catch (err) {
                    // Pointer capture can fail if the pointer is already gone.
                }
                element.classList.add("active");
                onDown(event.pointerId);
            },
            { passive: false },
        );

        const release = (event) => {
            event.preventDefault();
            event.stopPropagation();
            element.classList.remove("active");
            onUp(event.pointerId);
        };

        element.addEventListener("pointerup", release, { passive: false });
        element.addEventListener("pointercancel", release, { passive: false });
        element.addEventListener("lostpointercapture", release, {
            passive: false,
        });
        element.addEventListener("contextmenu", (event) => event.preventDefault());
    }

    bindTouch() {
        document.querySelectorAll(".dpad-btn").forEach((button) => {
            const keyCode = DIR_KEY_MAP[button.getAttribute("data-dir")];
            this.bindHoldButton(
                button,
                (pointerId) => this.setVirtualKey(keyCode, pointerId, true),
                (pointerId) => this.setVirtualKey(keyCode, pointerId, false),
            );
        });

        const digButton = document.getElementById("btn-dig");
        if (digButton) {
            digButton.addEventListener(
                "pointerdown",
                (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    try {
                        digButton.setPointerCapture(event.pointerId);
                    } catch (err) {
                        // Pointer capture can fail if the pointer is already gone.
                    }
                    digButton.classList.add("active");
                    this.onDig();
                },
                { passive: false },
            );

            const releaseDig = (event) => {
                event.preventDefault();
                event.stopPropagation();
                digButton.classList.remove("active");
            };
            digButton.addEventListener("pointerup", releaseDig, { passive: false });
            digButton.addEventListener("pointercancel", releaseDig, {
                passive: false,
            });
            digButton.addEventListener("lostpointercapture", releaseDig, {
                passive: false,
            });
            digButton.addEventListener("contextmenu", (event) =>
                event.preventDefault(),
            );
        }

        const resetButton = document.getElementById("btn-reset");
        if (resetButton) {
            resetButton.addEventListener(
                "pointerdown",
                (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    try {
                        resetButton.setPointerCapture(event.pointerId);
                    } catch (err) {
                        // Pointer capture can fail if the pointer is already gone.
                    }
                    resetButton.classList.add("active");
                    this.onReset();
                },
                { passive: false },
            );

            const releaseReset = (event) => {
                event.preventDefault();
                event.stopPropagation();
                resetButton.classList.remove("active");
            };
            resetButton.addEventListener("pointerup", releaseReset, {
                passive: false,
            });
            resetButton.addEventListener("pointercancel", releaseReset, {
                passive: false,
            });
            resetButton.addEventListener("lostpointercapture", releaseReset, {
                passive: false,
            });
            resetButton.addEventListener("contextmenu", (event) =>
                event.preventDefault(),
            );
        }
    }

    bindGestures() {
        document.addEventListener(
            "gesturestart",
            (event) => event.preventDefault(),
            { passive: false },
        );
        document.addEventListener(
            "gesturechange",
            (event) => event.preventDefault(),
            { passive: false },
        );
        document.addEventListener(
            "dblclick",
            (event) => event.preventDefault(),
            { passive: false },
        );
    }

    readMoveInput() {
        let forward = 0;
        let strafe = 0;
        if (this.keys.KeyW || this.keys.ArrowUp) forward += 1;
        if (this.keys.KeyS || this.keys.ArrowDown) forward -= 1;
        if (this.keys.KeyD || this.keys.ArrowRight) strafe += 1;
        if (this.keys.KeyA || this.keys.ArrowLeft) strafe -= 1;
        return { forward, strafe };
    }

    clear() {
        for (const key in this.keys) this.keys[key] = false;
        for (const key in this.virtualKeyOwners) {
            this.virtualKeyOwners[key].clear();
            this.keys[key] = false;
        }
        document
            .querySelectorAll(".dpad-btn.active, #btn-dig.active, #btn-reset.active")
            .forEach((element) => element.classList.remove("active"));
    }
}
